<template>
  <section>
    <h2>行列式の計算</h2>
    <div v-for="(entry,i) in history2" :key="i" class="container">
      <div class="math">
	<span v-if="i>0" class="equal"> = </span>
	<Exp :term="entry.term"></Exp>
      </div>
      <ul class="history">
	<li v-for="op of entry.ops" :key="op.id">
	  {{ op.op.txt(entry.term.num>1,false) }}
	  <button @click="cancel(op.id)"> 取消 </button>
	</li>
      </ul>
    </div>
    <div class="math" ref="main">
      <span v-if="history.length != 0" class="equal"> = </span>
      <Exp :term="root" :op="action"></Exp>
    </div>
    <ul>
      <li v-if="root.canCalc" :class="{selected: action.type=='calc'}">
	<label for="calc">[q]<input type="radio" v-model="action.type" value="calc" id="calc" ref="key_q"/> スカラーの演算
	<button v-if="message=='' && action.type == 'calc' && root.canCalc" class="exec" @click="exec"> 実行 </button> </label>
      </li>
      <li v-if="root.num != 0">
	<span v-if="root.num != 1">
	  [w]
	  <input type="number" min="1" :max="root.num" v-model.number="action.num" ref="key_w" />
	  番目の
	</span>
	行列の変形 :
	<ul>
	  <li v-if="root.canDeg(action.num)" :class="{selected: action.type=='deg'}">
	    <label for="deg">[e]<input type="radio" v-model="action.type" value="deg" id="deg" ref="key_e"/> 次数下げ
	      <button v-if="message=='' && action.type == 'deg'" class="exec" @click="exec"> 実行 </button></label>
	  </li>
	  <li v-if="root.size(action.num) == 2 || root.size(action.num) == 3" :class="{selected: action.type=='surrus'}">
	    <label for="surrus">[r]<input type="radio" v-model="action.type" value="surrus" id="surrus" ref="key_r"/> サラスの方法 </label>
	    <button v-if="message=='' && action.type == 'surrus'" class="exec" @click="exec"> 実行 </button>
	  </li>
	  <li>
	    [t]<select v-model="action.rc" ref="key_t">
	      <option value="row">行</option>
	      <option value="col">列</option>
	    </select>
	    に関する変形 :
	    <ul>
	      <li v-if="root.size(action.num) != 2" :class="{selected: action.type=='expand'}">
		<label for="expand">[a]<input type="radio" v-model="action.type" value="expand" id="expand"/>
		  <InputNum :op="action" prop="expand_i" :size="root.size(action.num)" ref="key_a"></InputNum> に関して展開する。
		  <button v-if="message=='' && action.type == 'expand'" class="exec" @click="exec"> 実行 </button>
		</label>
	      </li>
	      <li :class="{selected: action.type=='mult'}">
		<label for="mult">[s]<input type="radio" v-model="action.type" value="mult" id="mult"/>
		  <InputNum :op="action" prop="mult_i" :size="root.size(action.num)" ref="key_s"></InputNum> を
		  <InputCoeff :op="action" prop="coeff_mult"></InputCoeff>倍する。
		  <span v-if="invertible">({{action.coeff_mult.frac.inverse().toFraction()}}をくくり出す。)</span>
		  <button v-if="message=='' && action.type == 'mult'" class="exec" @click="exec"> 実行 </button>
		</label>
	      </li>
	      <li :class="{selected: action.type=='swap'}">
		<label for="swap">[d]<input type="radio" v-model="action.type" value="swap" id="swap"/>
		  <InputNum :op="action" prop="swap_i1" :size="root.size(action.num)" ref="key_d"></InputNum> と
		  <InputNum :op="action" prop="swap_i2" :size="root.size(action.num)"></InputNum> を入れ替える。
		  <button v-if="message=='' && action.type == 'swap'" class="exec" @click="exec"> 実行 </button>
		</label>
	      </li>
	      <li :class="{selected: action.type=='add'}">
		<label for="add">[f]<input type="radio" v-model="action.type" value="add" id="add"/>
		  <InputNum :op="action" prop="add_i1" :size="root.size(action.num)" ref="key_f"></InputNum> の
		  <InputCoeff :op="action" prop="coeff_add"></InputCoeff>倍を
		  <InputNum :op="action" prop="add_i2" :size="root.size(action.num)"></InputNum> に加える。
		  <button v-if="message=='' && action.type == 'add'" class="exec" @click="exec"> 実行 </button>
		</label>
	      </li>
	    </ul>
	  </li>
	</ul>
      </li>
    </ul>
    <p>
      <span class="warning">{{message}}</span>
    </p>
    <div v-if="tex">
      <span v-for="(line,i) of texSource" :key="i">
	{{line}} <br />
      </span>
    </div>
  </section>
</template>

<script>
import Fraction from '@/lib/fraction.js';
import { Operation, zero, one } from '@/lib/base.js';
import { Term } from '@/lib/det.js';
import Exp from '@/components/Exp.vue'
import InputNum from '@/components/InputNum.vue'
import InputCoeff from '@/components/InputCoeff.vue'

export default {
  name: 'Determinant',
  components: {
    Exp,
    InputNum,
    InputCoeff
  },
  props: {
    original: Array
  },
  data: function(){
    return {
      action: new Operation(),
      root: new Term(new Fraction(1)),
      history: [],
      tex: false
    }
  },
  created: function(){
    for(let i=0; i<this.original.length; i++){
      const row = [];
      for(let j=0; j<this.original.length; j++){
	row.push(this.original[i][j].frac.clone());
      }
      this.root.matrix.push(row);	
    }
    this.root.renumber(0);
    this.root.root = true;
  },
  mounted: function(){
    this.$refs.main.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
  },
  methods: {
    exec(){
      this.history.push({
	term: this.root,
	op: this.action
      });
      this.root = this.root.apply(this.action);
      this.action = new Operation();
      this.$nextTick( () => {this.$refs.main.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});});
    },
    cancel(i){
      this.root = this.history[i].term;
      this.action = this.history[i].op;
      this.history.splice(i);
    },
    random(){
      for(let i=0;i<this.size;i++){
	for(let j=0; j<this.size;j++){
	  this.original[i][j].input = '' + Math.floor(Math.random()*20-9);
	}
      }
    },
    keyUp(event){
      if('qwertasdf'.includes(event.key) && this.$refs['key_' + event.key]){
	const dict = {
	  'q': 'calc',
	  'e': 'deg',
	  'r': 'surrus',
	  'a': 'expand',
	  's': 'mult',
	  'd': 'swap',
	  'f': 'add'
	};
	if(this.$refs['key_' + event.key].$refs){
	  this.$refs['key_' + event.key].$refs.window.focus();
	  if('asdf'.includes(event.key)){
	    this.$refs['key_' + event.key].$refs.window.setSelectionRange(0,10);	    
	  }
	}else{
	  this.$refs['key_' + event.key].focus();
	}
	if(event.key in dict){
	  this.action.type = dict[event.key];
	}else if(event.key == 't'){
	  this.action.rc = (this.action.rc == 'row') ? 'col' : 'row';
	}
      }else if(event.key == '.'){
	this.tex = ! this.tex;
      }
    },
    invalidNum(i){
      return ! Number.isInteger(i) || i < 0 || i >= this.root.size(this.action.num) ;
    }
  },
  computed: {
    history2(){
      const ar = [];
      for(let i=0; i<this.history.length; i++){
	const entry = this.history[i];
	if(i>0 && entry.op.type == 'add' && this.history[i-1].op.type == 'add'
	   && entry.op.num == this.history[i-1].op.num && entry.op.rc == this.history[i-1].op.rc
	   && entry.op.add_i1 == this.history[i-1].op.add_i1){
	  ar[ar.length-1].ops.push({ op: entry.op, id: i });
	}else{
	  ar.push({
	    term: entry.term,
	    ops: [ { op: entry.op, id: i } ]
	  })
	}
      }
      return ar;
    },
    invertible(){
      return ! this.action.coeff_mult.frac.equals(zero);
    },
    message(){
      if(! Number.isInteger(this.action.num) || this.action.type != 'calc' && (this.action.num <= 0 || this.action.num > this.root.num)){
	return '行列の指定に誤りがあります。';
      }else if(this.action.type == 'mult' && this.action.coeff_mult.error || this.action.type == 'add' && this.action.coeff_add.error){
	return 'スカラー(有理数)の入力に誤りがあります。';
      }else if(this.action.type == 'mult' && this.action.coeff.equals(zero)){
	return '0をかけることはできません。';
      }else if(this.action.type == 'expand' && this.invalidNum(this.action.expand_i)
	       || this.action.type == 'mult' && this.invalidNum(this.action.mult_i)
	       || this.action.type == 'swap' && (this.invalidNum(this.action.swap_i1) || this.invalidNum(this.action.swap_i2))
	       || this.action.type == 'add' && (this.invalidNum(this.action.add_i1) || this.invalidNum(this.action.add_i2)) ){
	return this.action.rcTxt + '番号に誤りがあります。';	
      }else if(this.action.type == 'swap' && this.action.swap_i1 == this.action.swap_i2){
	return this.action.rcTxt + '番号が同一です。';
      }else if(this.action.type == 'add' && this.action.add_i1 == this.action.add_i2){
	return this.action.rcTxt + '番号が同一です。';
      }else if(this.action.type == 'add' && this.action.coeff.equals(zero) || this.action.type == 'mult' && this.action.coeff.equals(one)){
	return 'この操作で行列は変化しません。';
      }else{
	return '';
      }
    },
    texSource(){
      let ar = [];
      ar.push('\\begin{align*}');
      ar.push('&\\phantom{=}');
      for(const entry of this.history2){
	ar = ar.concat(entry.term.tex);
	let txt = '& &\\text{';
	for(const obj of entry.ops){
	  const op = obj.op;
	  txt += op.txt(entry.term.num>1,false)	  
	}
	txt +='}\\\\';
	ar.push(txt);
	ar.push('&=');
      }
      ar = ar.concat(this.root.tex);
      ar.push('\\end{align*}');
      return(ar);
    }
  }
}
</script>

<style scoped>
</style>
